import cx from 'clsx';
import Image from 'next/image';

import type { Stock } from '@/apis/server/new-car/v1/car-detail';
import { ContactItem } from '@/components/contact-item';
import { TextLink } from '@/components/text-link';
import { Tooltip } from '@/components/tooltip';
import { getDesktopContactNumbers } from '@/utils/dealerContacts/util';
import { joinSgcmMedia, joinSgcmSite } from '@/utils/url/join/public';

import { ICON_SIZE_IN_PIXEL } from './constants';
import styles from './styles.module.scss';
import type { Component } from './types';

export const Item: Component = ({
  parallelDealer,
  submodelList,
}) => {
  const {
    id, name, hotlines, adverts, whatsAppNumber,
  } = parallelDealer;

  const filteredAdverts = adverts.filter(advert => advert.stocks.length !== 0);

  const noContact = hotlines.length === 0 && !whatsAppNumber;
  const hasWa = whatsAppNumber;

  const {
    hotlines: desktopHotlines,
  } = getDesktopContactNumbers(hotlines, whatsAppNumber);
  const hasMultiContact = desktopHotlines.length > 1;

  const toTitleCase = (str: string) => str
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());

  return (
    <div className={styles.containerRoot}>
      <div className={cx(
        styles.containerColumn,
        styles.containerDealer,
      )}>
        <TextLink
          className={styles.textPiName}
          href={`${joinSgcmSite(`/new_cars/newcars_PIdealer.php?dcode=${id}`)}`}
          target="_blank"
          text={name} />
        {!noContact && (
          <div className={cx(
            styles.containerContact,
            hasMultiContact && styles.isMultiRow,
          )}>
            <ContactItem
              contactType="hotline"
              displayMode="default"
              fontSize="small"
              iconSize={16}
              list={desktopHotlines.slice().reverse()}
              seperator="|" />
            {hasWa && (
              <ContactItem
                contactType="whatsApp"
                displayMode="default"
                fontSize="small"
                iconSize={16}
                list={[whatsAppNumber]}
                target="_blank" />
            )}
          </div>
        )}
      </div>
      <div className={cx(
        styles.containerColumn,
        styles.containerRightColumn,
      )}>
        {filteredAdverts.map((advert) => {
          const submodelName = submodelList.find(
            submodel => submodel.id === advert.subModelId,
          )?.name;

          const groupedStock = advert.stocks.reduce<Stock[]>((acc, { colour, unit }) => { // @suppress longLineCheck
            const normalizedColour = toTitleCase(colour);

            const existing = acc.find(item => item.colour === normalizedColour);

            if (existing) {
              existing.unit += unit;
            } else {
              acc.push({ colour: normalizedColour, unit });
            }

            return acc;
          }, []);

          return (
            <div
              key={`submodel-${advert.subModelId}`}
              className={styles.conatinerSubModelContent}>
              <div className={styles.containerStocks}>
                <div className={styles.subModelName}>
                  {submodelName}
                </div>
                {groupedStock.map(stock => (
                  <div
                    key={`stock-${advert.id}`}
                    className={styles.textStock}>
                    {`${stock.colour} color - ${stock.unit} unit available`}
                  </div>
                ))}
              </div>
              <div className={styles.containerPrice}>
                <span className={styles.priceText}>
                  $
                  {advert.price.toLocaleString()}
                </span>
                {advert.remark && advert.remark.length !== 0 && (
                  <Tooltip
                    className={cx(
                      styles.containerTooltip,
                      styles.containerRemarkTooltip,
                    )}
                    content={(
                      <div className={cx(
                        styles.containerTooltipContent,
                      )}>
                        {advert.remark}
                      </div>
                    )}>
                    <Image
                      alt="icon exclamation mark"
                      className={styles.iconExclamationMark}
                      height={ICON_SIZE_IN_PIXEL}
                      src={`${joinSgcmMedia('/public/new-cars/icon/information.svg')}`}
                      width={ICON_SIZE_IN_PIXEL} />
                  </Tooltip>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
